/**
 * MessageGroup Component - HYB Shop Chat
 * Groups consecutive messages from same sender within time threshold
 */

import { useMemo } from 'react';
import { cn } from '../../../utils/responsive';
import { MessageBubble } from './MessageBubble';
import { MessageStatus } from '../ReadReceipt';

interface Message {
  id: string;
  isAdmin: boolean;
  text?: string;
  images?: string[];
  type?: 'text' | 'image';
  createdAt?: string;
  status?: MessageStatus;
}

interface MessageGroupProps {
  messages: Message[];
  className?: string;
}

/**
 * Check if two messages should be grouped (same sender, within 2 minutes)
 */
function shouldGroup(msg1: Message, msg2: Message): boolean {
  if (msg1.isAdmin !== msg2.isAdmin) return false;

  if (!msg1.createdAt || !msg2.createdAt) return true;

  const time1 = new Date(msg1.createdAt).getTime();
  const time2 = new Date(msg2.createdAt).getTime();
  const diffMinutes = Math.abs(time2 - time1) / (1000 * 60);

  return diffMinutes <= 2;
}

/**
 * Group messages by sender and time proximity
 */
function groupMessages(messages: Message[]): Message[][] {
  if (messages.length === 0) return [];

  const groups: Message[][] = [];
  let currentGroup: Message[] = [messages[0]];

  for (let i = 1; i < messages.length; i++) {
    const prevMessage = messages[i - 1];
    const currentMessage = messages[i];

    if (shouldGroup(prevMessage, currentMessage)) {
      currentGroup.push(currentMessage);
    } else {
      groups.push(currentGroup);
      currentGroup = [currentMessage];
    }
  }

  groups.push(currentGroup);
  return groups;
}

/**
 * Get position of message within a group
 */
function getPosition(index: number, total: number): 'first' | 'middle' | 'last' | 'single' {
  if (total === 1) return 'single';
  if (index === 0) return 'first';
  if (index === total - 1) return 'last';
  return 'middle';
}

export function MessageGroup({ messages, className }: MessageGroupProps) {
  const groups = useMemo(() => groupMessages(messages), [messages]);

  return (
    <div className={cn('space-y-4', className)}>
      {groups.map((group, groupIndex) => {
        const isAdmin = group[0].isAdmin;

        return (
          <div
            key={`group-${groupIndex}`}
            className={cn(
              'flex flex-col',
              isAdmin ? 'items-end' : 'items-start',
              'space-y-0.5'
            )}
          >
            {group.map((message, msgIndex) => (
              <MessageBubble
                key={message.id}
                message={message}
                isAdmin={isAdmin}
                position={getPosition(msgIndex, group.length)}
                showTimestamp={msgIndex === group.length - 1}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default MessageGroup;
